// Copyright (c) 2025 Audere, All Rights Reserved

import { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Drawer, useMediaQuery } from "@mui/material";
import { generatePath, useNavigate, useParams, useLocation } from "react-router";

import { createStyleSheet } from "../utils/styles";
import DashboardSidebar from "../components/DashboardSidebar";
import { theme } from "../../theme";
import TabSwitcher from "../components/TabSwitcher";
import EvaluationSetup from "../components/EvaluationSetup";
import ReportsContainer from "../components/ReportsContainer";
import type {
  GetProjectData,
  ProjectData,
  ProjectModel,
  SelectedTestData,
  TestData,
} from "../types/projects";
import { useLazyGetProjectQuery, useListProjectsQuery } from "../features/data/projectApiSlice";
import { DASHBOARD_PATH } from "../utils/constants";
import { useSelector } from "react-redux";
import { selectCurrentOrganizationId } from "../features/auth/authSlice";
import GenerateSDkDialog from "../components/GenerateSDKDialog";
import FieldEvaluation from "../components/FieldEvaluation";

const Dashboard = () => {
  const isBelowTabletSize = useMediaQuery("(max-width:768px)");
  const currentOrganizationId = useSelector(selectCurrentOrganizationId);
  const params = useParams();
  const { data: listProjectsData } = useListProjectsQuery();
  const location = useLocation();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState<number>(0);
  const [isValidationSetDashboard, setIsValidationSetDashboard] = useState<boolean>(false);
  const [selectedTestData, setSelectedTestData] = useState<SelectedTestData>();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState<ProjectData>();
  const [selectedTest, setSelectedTest] = useState<TestData>();
  const [isGenerateSdkDialog, setIsGenerateSdkDialog] = useState(false);

  const [getProject, { data: getProjectData }] = useLazyGetProjectQuery();
  const [projectData, setProjectData] = useState<GetProjectData>();
  const onSelectRDTTest = useCallback(
    (test: TestData, projectData: GetProjectData) => {
      const model = (projectData?.project?.models.slice().reverse() ?? []).find(
        data => data.organization_uid === test.organization_uid
      );

      setProjectData(projectData);
      const selectedTestData: SelectedTestData = {
        models: model ? [model] : [],
        rdt: test,
        name: projectData?.project?.name,
        projectUid: projectData?.project?.project_uid,
      };

      setSelectedTestData(selectedTestData);
    },
    [setSelectedTestData]
  );
  const toggleMobileDrawer = useCallback(() => {
    if (isBelowTabletSize) {
      setIsDrawerOpen(prev => !prev);
    }
  }, [isBelowTabletSize]);
  const memoizedSidebar = useMemo(
    () => (
      <DashboardSidebar
        isBelowTabletSize={isBelowTabletSize}
        isDrawerOpen={isDrawerOpen}
        onSelectRDTTest={onSelectRDTTest}
        onDrawerToggle={toggleMobileDrawer}
        getProjectData={getProjectData}
        selectedTest={selectedTest}
        setSelectedTest={setSelectedTest}
        selectedProject={selectedProject}
        setSelectedProject={setSelectedProject}
        listProjectsData={listProjectsData}
      />
    ),
    [
      onSelectRDTTest,
      toggleMobileDrawer,
      isBelowTabletSize,
      isDrawerOpen,
      getProjectData,
      selectedTest,
      selectedProject,
      listProjectsData,
    ]
  );
  const handleGenerateSdkDialogClose = useCallback(() => {
    setIsGenerateSdkDialog(false);
    navigate(".", { replace: true, state: {} });
  }, [navigate]);

  const handleRDTSelection = useCallback(
    (rdtUid: string) => {
      if (!selectedProject?.project_uid) return;
      getProject({ projectUid: selectedProject.project_uid })
        .unwrap()
        .then(response => {
          const tests = response.project?.rdts ?? [];
          const matchedTest = tests.find(test => test.rdt_uid === rdtUid);
          if (!matchedTest) return;
          setSelectedTest(matchedTest);
          onSelectRDTTest(matchedTest, response);
          navigate(
            generatePath(DASHBOARD_PATH, {
              orgUid: currentOrganizationId,
              projectUid: selectedProject.project_uid,
              testUid: matchedTest.rdt_uid,
            })
          );
        });
    },
    [selectedProject?.project_uid, currentOrganizationId, getProject, navigate, onSelectRDTTest]
  );

  useEffect(() => {
    const { projectUid } = params;
    if (!projectUid || !listProjectsData?.projects) return;
    const project = listProjectsData.projects.find(project => project.project_uid === projectUid);
    if (!project) return;
    setSelectedProject(project);
  }, [params, listProjectsData?.projects, currentOrganizationId]);
  useEffect(() => {
    if (selectedProject?.project_uid) {
      getProject({ projectUid: selectedProject.project_uid })
        .unwrap()
        .then(response => {
          const test = response.project?.rdts?.[0];
          if (!test) return;
          setSelectedTest(test);
          onSelectRDTTest(test, response);
          navigate(
            generatePath(DASHBOARD_PATH, {
              orgUid: currentOrganizationId,
              projectUid: selectedProject.project_uid,
              testUid: test.rdt_uid,
            })
          );
        });
    }
  }, [selectedProject, currentOrganizationId, navigate, getProject, onSelectRDTTest]);
  useEffect(() => {
    if (location.state?.openGenerateSdkDialog) {
      setIsGenerateSdkDialog(true);
    }
  }, [location.state]);
  const latestModel = useMemo(() => {
    const models = projectData?.project.models;
    if (!models?.length) return null;

    const getTimestamp = (m: ProjectModel) => new Date(m.training?.end_timestamp || 0).getTime();
    return models.reduce((latest, current) => {
      const latestTs = getTimestamp(latest);
      const currentTs = getTimestamp(current);
      return currentTs > latestTs ? current : latest;
    }, models[0]);
  }, [projectData?.project.models]);

  useEffect(() => {
    const models = projectData?.project?.models;
    if (!models || models.length === 0) {
      setIsValidationSetDashboard(false);
      return;
    }
    const allComplete =
      latestModel?.training?.status === "SUCCESS" && latestModel?.validation?.status === "SUCCESS";
    setIsValidationSetDashboard(allComplete);
  }, [projectData?.project?.models, projectData, latestModel]);
  useEffect(() => {
    setProjectData(getProjectData);
  }, [getProjectData]);
  return (
    <Box sx={styles.container}>
      {isBelowTabletSize && isDrawerOpen ? (
        <Drawer
          anchor="left"
          open={isDrawerOpen}
          ModalProps={{
            keepMounted: true,
          }}
          sx={styles.drawerPaper}
        >
          {memoizedSidebar}
        </Drawer>
      ) : (
        memoizedSidebar
      )}

      <Box sx={styles.rightPaneContainer}>
        <Box sx={styles.rightPaneInnerContainer}>
          <TabSwitcher activeTab={activeTab} setActiveTab={setActiveTab}></TabSwitcher>
          {activeTab === 0 ? (
            !isValidationSetDashboard && projectData ? (
              <EvaluationSetup
                projectDetails={projectData}
                latestModel={latestModel}
                data-testid="evaluation-setup"
              />
            ) : (
              <ReportsContainer
                selectedTest={selectedTestData}
                handleRDTSelection={handleRDTSelection}
                latestModel={latestModel}
                data-testid="reports-container"
              />
            )
          ) : (
            <FieldEvaluation selectedTest={selectedTestData} />
          )}
        </Box>
      </Box>
      <GenerateSDkDialog open={isGenerateSdkDialog} onClose={handleGenerateSdkDialogClose} />
    </Box>
  );
};

const styles = createStyleSheet({
  container: {
    width: "100%",
    height: "100%",
    display: "flex",
    flexDirection: "row",
    background: theme.palette.detailBackground.main,
    overflowY: "auto",
  },
  rightPaneContainer: {
    flex: 1,
    background: theme.palette.detailBackground.main,
    overflowY: "auto",
  },
  rightPaneInnerContainer: {
    display: "flex",
    alignItems: "center",
    padding: "10px",
    flexDirection: "column",
    height: "100%",
  },
  drawerPaper: {
    "& .MuiDrawer-paper": {
      width: theme.spacing(43),
    },
  },
});

export default Dashboard;
